import React, { useState, useEffect } from 'react';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 40 }, (_, i) => currentYear - i);

const DateRangePicker = ({ value, onChange, placeholder }) => {
  const parseDate = (str) => {
    if (!str) return { fromMonth: '', fromYear: '', toMonth: '', toYear: '', isCurrent: false };
    const parts = str.split(' - ');
    const from = parts[0]?.trim() || '';
    const to = parts[1]?.trim() || '';
    const isCurrent = to.toLowerCase() === 'présent' || to.toLowerCase() === 'present' || to.toLowerCase() === "aujourd'hui";

    const parseMonthYear = (s) => {
      const tokens = s.split(' ');
      if (tokens.length === 2) {
        const monthIdx = MONTHS.findIndex(m => m.toLowerCase() === tokens[0].toLowerCase());
        return { month: monthIdx >= 0 ? String(monthIdx) : '', year: tokens[1] };
      }
      if (tokens.length === 1 && /^\d{4}$/.test(tokens[0])) return { month: '', year: tokens[0] };
      return { month: '', year: '' };
    };

    const f = parseMonthYear(from);
    const t = isCurrent ? { month: '', year: '' } : parseMonthYear(to);

    return { fromMonth: f.month, fromYear: f.year, toMonth: t.month, toYear: t.year, isCurrent };
  };

  const [data, setData] = useState(() => parseDate(value));

  useEffect(() => {
    setData(parseDate(value));
  }, [value]);

  const buildString = (d) => {
    const fromStr = d.fromMonth !== '' && MONTHS[parseInt(d.fromMonth)]
      ? `${MONTHS[parseInt(d.fromMonth)]} ${d.fromYear}`
      : d.fromYear || '';
    if (!fromStr) return '';
    if (d.isCurrent) return `${fromStr} - Présent`;
    const toStr = d.toMonth !== '' && MONTHS[parseInt(d.toMonth)]
      ? `${MONTHS[parseInt(d.toMonth)]} ${d.toYear}`
      : d.toYear || '';
    return toStr ? `${fromStr} - ${toStr}` : fromStr;
  };

  const update = (key, val) => {
    const next = { ...data, [key]: val };
    if (key === 'isCurrent' && val) { next.toMonth = ''; next.toYear = ''; }
    setData(next);
    onChange(buildString(next));
  };

  const selectClass = "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs px-2 py-1.5 focus:ring-2 focus:ring-purple-300 dark:focus:ring-purple-700 focus:border-purple-500 outline-none text-gray-700 dark:text-gray-300";

  return (
    <div className="space-y-2" data-testid="date-range-picker">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-400 w-6 flex-shrink-0">De</span>
        <select value={data.fromMonth} onChange={(e) => update('fromMonth', e.target.value)} className={selectClass} data-testid="from-month">
          <option value="">Mois</option>
          {MONTHS.map((m, i) => <option key={i} value={String(i)}>{m}</option>)}
        </select>
        <select value={data.fromYear} onChange={(e) => update('fromYear', e.target.value)} className={selectClass} data-testid="from-year">
          <option value="">Année</option>
          {YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-400 w-6 flex-shrink-0">À</span>
        {!data.isCurrent && (
          <>
            <select value={data.toMonth} onChange={(e) => update('toMonth', e.target.value)} className={selectClass} data-testid="to-month">
              <option value="">Mois</option>
              {MONTHS.map((m, i) => <option key={i} value={String(i)}>{m}</option>)}
            </select>
            <select value={data.toYear} onChange={(e) => update('toYear', e.target.value)} className={selectClass} data-testid="to-year">
              <option value="">Année</option>
              {YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          </>
        )}
        <label className="flex items-center gap-1.5 ml-1 cursor-pointer">
          <input type="checkbox" checked={data.isCurrent} onChange={(e) => update('isCurrent', e.target.checked)}
            className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            data-testid="current-checkbox"
          />
          <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">Aujourd'hui</span>
        </label>
      </div>
    </div>
  );
};

export default DateRangePicker;
